import { useState, useCallback, useRef, useEffect } from 'react';
import { playQuestionAudio, stopAudio } from '@/lib/audio/player';

interface UseAudioPlayerReturn {
  isPlaying: boolean;
  isLoading: boolean;
  error: string | null;
  playQuestion: (questionId: number) => Promise<void>;
  stop: () => Promise<void>;
  clearError: () => void;
}

export function useAudioPlayer(): UseAudioPlayerReturn {
  const [isPlaying, setIsPlaying] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const mountedRef = useRef(true);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      stopAudio();
    };
  }, []);

  const playQuestion = useCallback(async (questionId: number) => {
    setError(null);
    setIsLoading(true);
    try {
      setIsPlaying(true);
      await playQuestionAudio(questionId);
    } catch {
      if (mountedRef.current) setError('audio_playback_failed');
    } finally {
      if (mountedRef.current) {
        setIsPlaying(false);
        setIsLoading(false);
      }
    }
  }, []);

  const stop = useCallback(async () => {
    await stopAudio();
    if (mountedRef.current) {
      setIsPlaying(false);
      setIsLoading(false);
    }
  }, []);

  const clearError = useCallback(() => setError(null), []);

  return { isPlaying, isLoading, error, playQuestion, stop, clearError };
}
